import { withTransaction } from '@elastic/apm-rum-react';
import TextArea from 'antd/lib/input/TextArea';
import {
  Button,
  Input,
  Loader,
  ModalWindow,
  RadioButton,
  Select,
} from 'components';
import { useStateVar } from 'helpers/useStateVar';
import { getUserProfile } from 'helpers/userProfile';
import { t } from 'i18next';
import { ArrowIcon } from 'icons';
import { apiCall, apiCallFormData } from 'providers';
import queryString from 'query-string';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import { Box, Flex } from 'reflexbox';
import { isDevice } from '~/helpers/devices';
import {
  getCitiesList, getClientsList, getStatesList, getUnitsList,
} from '~/helpers/genericHelper';
import { optional } from '~/helpers/requestFilters';
import { StatusIcon } from '~/pages/Analysis/Utilities/UtilityFilter/styles';
import { AdminLayout } from '../AdminLayout';
import { DevsTable } from './DevsTable';
import { Filters } from './Filters';
import { checkCanUpdate, checkCanUpdateMigration, getDeviceVersionGroupKey } from './rules';
import {
  FileInput,
  FwVersTable,
  ModalContent,
  ViewMoreArrow,
} from './styles';
import { DeviceRow, FirmwareRow, PageState } from './types';

interface UpperControlsProps {
  pageState: PageState,
  render: () => void,
  onSearchDelete: (i: number) => void,
  onSearchAddition: (tag: { text: string }) => void,
  onClickFilter: () => void,
  clearFilters: () => void,
  isLoading: boolean
}

export const AdmFirmware = () => {
  const history = useHistory();
  const [state, render, setState] = useStateVar(() => {
    const { preFiltered } = queryString.parse(history.location.search);
    const state: PageState = {
      searchState: preFiltered ? [{ text: preFiltered.toString() }] : [],
      clients: [],
      units: [],
      cities: [],
      states: [],
      connections: [
        { value: 'ONLINE', name: t('online'), icon: <StatusIcon status="ONLINE" /> },
        { value: 'LATE', name: t('late'), icon: <StatusIcon status="LATE" /> },
        { value: 'OFFLINE', name: t('offline'), icon: <StatusIcon status="OFFLINE" /> },
      ],
      selectedCities: [],
      selectedClients: [],
      selectedConnections: [],
      selectedUnits: [],
      selectedStates: [],
      allDevs: [],
      filteredMachines: [],
      isLoading: false,
      isLoadingVersions: false,
      waitingRequest: false,
      csvData: [],
      fwVersionsAllTypes: [],
      fwVersionsRows: [],
      fwCols: [],
      fwFolders: [],
      limitItems: 50,
      lastScrollTop: 0,
      openModal: null,
      modalReqUpdate: {
        fw: {},
        sending: false,
      },
      modalUpdateResult: {
        devs: [],
      },
      modalReqVersResult: {
        devs: [],
      },
      modalReqCertResult: {
        devs: [],
      },
      fwStageFilter: 'prod',
      renderUpper: () => {}, // will be replaced
      renderDevsTable: () => {}, // will be replaced
    };

    return state;
  });

  function populateRefNameTranslated(rows: FirmwareRow[]): FirmwareRow[] {
    return rows.map((item) => ({
      ...item,
      refNameTranslated: item.refName ? item.refName.replace(/to/gi, 'para') : null,
    }));
  }

  const handleSearchDelete = useCallback((i: number) => {
    setState({
      searchState: state.searchState.filter((_, index) => index !== i),
    });
  }, []);

  const handleSearchAddition = useCallback((tag: { text: string }) => {
    setState({
      searchState: [...state.searchState, tag],
    });
  }, []);

  async function handleGetFwVersions(): Promise<void> {
    try {
      setState({ isLoadingVersions: true });

      const { list: _fwsList, fwHwRevs: fwFamilies } = await apiCall('/devs/get-firmware-versions-v2', {});
      const fwsList: FirmwareRow[] = _fwsList;

      const fwColumns: string[] = (fwFamilies || []).map((x) => x.toUpperCase());
      for (const fw of fwsList) {
        fw.fDate = new Date(new Date(fw.date).getTime() - 3 * 60 * 60 * 1000).toISOString().substr(0, 19).replace('T', ' ');
        if (!fw.fwFamily) continue;
        const fCol = fw.fwFamily.toUpperCase();
        if (!fwColumns.includes(fCol)) {
          fwColumns.push(fCol);
        }
        fw.fPath = `[${fw.fwFamily}] ${fw.fwVers}`;
      }

      state.fwCols = [...fwColumns].sort((a, b) => a.localeCompare(b));
      state.fwFolders = state.fwCols.map((col) => col.toLowerCase());
      state.fwVersionsAllTypes = fwsList;
      state.fwVersionsRows = state.fwVersionsAllTypes.filter((item) => (!state.fwStageFilter) || (item.fwType === state.fwStageFilter));

      state.fwVersionsRows = populateRefNameTranslated(state.fwVersionsRows);
    } catch (err) {
      console.log(err);
      toast.error(t('erroDadosMaquinas'));
    }

    await getOptions();
    state.isLoadingVersions = false;
    render();
  }

  async function handleGetData() {
    try {
      setState({ isLoading: true });
      const hasDeviceIds = state.searchState.every((item) => isDevice(item.text));

      const { list: devsList } = await apiCall('/devs/get-devs-fwinfo-v2', {
        DEV_IDS: hasDeviceIds ? state.searchState.map((item) => item.text) : undefined,
        CLIENT_IDS: optional(state.selectedClients as string[]),
        CONNECTION: optional(state.selectedConnections as string[]),
        STATE_IDS: optional(state.selectedStates as string[]),
        UNIT_IDS: optional(state.selectedUnits as string[]),
        CITY_IDS: optional(state.selectedCities as string[]),
      });

      const fwFolders: { [type: string]: true } = {};
      for (const fwFolder of state.fwCols) {
        fwFolders[fwFolder.toLowerCase()] = true;
      }

      const allDevs: DeviceRow[] = devsList;

      for (const dev of allDevs) {
        const { fwInfo, versionNumber } = dev;

        if (fwInfo && fwInfo.hardware_type) {
          fwFolders[fwInfo.hardware_type] = true;
        }
        const devIdFamily = dev.DEV_ID.substring(0, 4).toLowerCase();
        if ((devIdFamily.length === 4) && (devIdFamily[0] === 'd') && (devIdFamily[3] >= '0') && (devIdFamily[3] <= '9')) {
          dev.devIdFamily = devIdFamily;
        }

        dev.firmwareText = fwInfo ? `[${fwInfo.hardware_type}] ${fwInfo.firmware_version}` : '-';
        dev.checked = false;

        dev.col_firmware_s = fwInfo && `[${fwInfo.hardware_type}] ${fwInfo.firmware_version}`;
        dev.versionNumber = versionNumber || { vMajor: 0, vMinor: 0, vPatch: 0 };
      }
      state.allDevs = allDevs;
      state.fwFolders = Object.keys(fwFolders);
    } catch (err) {
      console.log(err);
      toast.error(t('erroDadosMaquinas'));
    }
    state.isLoading = false;
    render();
  }

  async function getOptions(): Promise<void> {
    const localState = {
      statesListOpts: [],
      citiesListOpts: [],
      unitsListOpts: [],
      clientsListOpts: [],
    };
    const localRender = () => {};

    await Promise.all([
      getStatesList(localState, localRender),
      getCitiesList(localState, localRender),
      getClientsList(localState, localRender),
      getUnitsList(localState, localRender),
    ]);

    setState({
      clients: localState.clientsListOpts,
      cities: localState.citiesListOpts,
      units: localState.unitsListOpts,
      states: localState.statesListOpts,
    });
  }

  function clearFilters(): void {
    setState({
      searchState: [],
      selectedUnits: [],
      selectedCities: [],
      selectedClients: [],
      selectedStates: [],
      selectedConnections: [],
    });
  }

  useEffect(() => {
    function onScroll() {
      if (!state.limitItems) return;
      if (state.limitItems >= state.filteredMachines.length) return;
      const scrollBottom = document.documentElement.scrollTop + window.innerHeight;
      const distanceToEnd = document.documentElement.scrollHeight - scrollBottom;
      if (!(distanceToEnd < 500)) return;
      if (state.lastScrollTop >= document.documentElement.scrollTop) return;
      setState({
        limitItems: state.limitItems + 200,
      });
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    handleGetFwVersions();
  }, []);

  return (
    <>
      <Helmet>
        <title>Diel Energia - Máquinas</title>
      </Helmet>
      <AdminLayout />
      {state.isLoadingVersions ? <Loader variant="primary" /> : (
        <>
          {/* Parte superior da página e modais (tudo que não é a tabela de dispositivos). Renderização mais leve. */}
          <UpperControls
            pageState={state}
            onSearchDelete={handleSearchDelete}
            onSearchAddition={handleSearchAddition}
            onClickFilter={handleGetData}
            isLoading={state.isLoading}
            render={render}
            clearFilters={clearFilters}
          />
          {/* Tabela dos dispositivos. Renderização pesada. */}
          <DevsTable pageState={state} loading={state.isLoading} />
        </>
      )}
    </>
  );
};

function UpperControls(props: Readonly<UpperControlsProps>) {
  const [profile] = useState(getUserProfile);
  const {
    pageState: state, onSearchAddition, onSearchDelete, render,
  } = props;
  const { t } = useTranslation();
  const [localState, renderUpper, setLocalState] = useStateVar({
    viewMoreFws: false,
    debounceTimer: null as null|NodeJS.Timeout,
    availableActions: [
      { label: 'Enviar Atualização', value: 'update' },
      { label: 'Solicitar Versão', value: 'fw-info' },
      { label: 'Solicitar Certificado', value: 'cert-info' },
    ],
    firmwareFamilies: {} as {
      // Tem que ter todos os hardwares que serão usados como título das colunas
      // Embaixo vai ter uma coluna por major
      [fwColName: string]: {
        [vMajorName: string]: {
          fwVers: string
          fPath: string
          fDate: string
          description?: string
          refName?: string
        }[]
      }
    },
  });
  state.renderUpper = renderUpper;

  const moreThanTen = state.fwVersionsRows.length > 10;
  const checkedDevs = state.filteredMachines.filter((dev) => dev.checked);

  function wantUploadFirmware() {
    state.openModal = 'ModalAddFirmware';
    state.renderUpper();
  }

  async function applyActionToSelected(action: string) {
    if (state.waitingRequest) return;
    if (!checkedDevs.length) return;
    if (action === 'update') {
      wantUpate();
    }
    if (action === 'fw-info') {
      wantGetFwInfo();
    }
    if (action === 'cert-info') {
      wantGetCertInfo();
    }
  }

  function wantUpate() {
    if (!checkedDevs.length) return;
    state.openModal = 'ModalSendOtaUpdate';
    state.renderUpper();
  }

  function wantGetFwInfo() {
    state.modalReqVersResult.devs = checkedDevs.map((dev) => ({
      devId: dev.DEV_ID,
    }));
    state.openModal = 'ModalReqVersResult';
    state.renderUpper();
  }

  function wantGetCertInfo() {
    state.modalReqCertResult.devs = checkedDevs.map((dev) => ({
      devId: dev.DEV_ID,
    }));
    state.openModal = 'ModalReqCertResult';
    state.renderUpper();
  }

  function populateRefNameTranslated(rows: FirmwareRow[]): FirmwareRow[] {
    return rows.map((item) => ({
      ...item,
      refNameTranslated: item.refName ? item.refName.replace(/to/gi, 'para') : null,
    }));
  }

  function onClickFilter(): void {
    props.onClickFilter();
  }

  useEffect(() => {
    state.fwVersionsRows = state.fwVersionsAllTypes.filter((item) => (!state.fwStageFilter) || (item.fwType === state.fwStageFilter));
    state.fwVersionsRows = populateRefNameTranslated(state.fwVersionsRows);
    renderUpper();
  }, [state.fwStageFilter]);

  useEffect(() => {
    localState.firmwareFamilies = {};

    for (const fwRow of state.fwVersionsRows) {
      const fwColName = fwRow.fwFamily.toUpperCase();

      // Verifica se tem refName no formato *_to_*
      let vMajorName = fwRow.versionNumber!.vMajor.toString(); // valor padrão

      if (fwRow.refName && fwRow.refName.includes('_to_')) {
        const parts = fwRow.refName.split('_to_');
        const lastPart = parts[parts.length - 1]; // pega tudo depois do último "to_"

        // Se o último valor estiver no formato vX, usa ele como subcoluna
        if (/^v\d+/.test(lastPart)) {
          vMajorName = lastPart; // substitui vMajor pelo valor de migração
        }
      }

      if (!localState.firmwareFamilies[fwColName]) {
        localState.firmwareFamilies[fwColName] = {};
      }

      const hwFirmwares = localState.firmwareFamilies[fwColName];

      if (!hwFirmwares[vMajorName]) {
        hwFirmwares[vMajorName] = [];
      }

      hwFirmwares[vMajorName].push({
        fwVers: fwRow.fwVers,
        fPath: fwRow.fPath!,
        fDate: fwRow.fDate!,
        refName: fwRow.refName,
        description: fwRow.description,
      });
    }

    renderUpper();
  }, [state.fwVersionsRows]);

  return (
    <>
      {/* Tabela de versões de firmware existentes, botão de upload e explicação das colunas */}
      <Flex flexDirection="column" style={{ padding: '20px 30px', width: '100%' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          {profile.permissions.isAdminSistema && (
            <div style={{ paddingTop: '30px' }}>
              <div>
                <Button style={{ width: '160px' }} onClick={wantUploadFirmware} variant="primary">Enviar Novo FW</Button>
              </div>
              <div style={{ marginTop: '15px' }}>
                <RadioButton checked={state.fwStageFilter === 'prod'} onClick={() => { state.fwStageFilter = 'prod'; renderUpper(); }} label="Produção" />
              </div>
              <div style={{ marginTop: '8px' }}>
                <RadioButton checked={state.fwStageFilter === 'test'} onClick={() => { state.fwStageFilter = 'test'; renderUpper(); }} label="Teste" />
              </div>
              <div style={{ marginTop: '8px' }}>
                <RadioButton checked={state.fwStageFilter === 'migration'} onClick={() => { state.fwStageFilter = 'migration'; renderUpper(); }} label="Migração" />
              </div>
            </div>
          )}
          <div style={{ width: '80%' }}>
            <FwVersTable limitSize={moreThanTen && !localState.viewMoreFws}>
              {state.fwCols.map((fwColName) => (
                <div
                  key={fwColName}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid lightgrey',
                    padding: '0 6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div style={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {fwColName}
                  </div>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    {Object.keys(localState.firmwareFamilies[fwColName] || {}).map((vMajorName) => (
                      <div key={vMajorName} style={{ display: 'flex', flexDirection: 'column' }}>
                        {localState.firmwareFamilies[fwColName][vMajorName].map((fw) => (
                          <div key={fw.fPath}>
                            <div style={{ marginTop: '6px', fontSize: '95%' }} data-tip data-for={fw.fPath}>
                              {fw.refName ? fw.refName : fw.fwVers}
                            </div>
                            <ReactTooltip
                              id={fw.fPath}
                              place="top"
                              effect="solid"
                              delayHide={100}
                              offset={{ top: 0, left: 10 }}
                              textColor="#000000"
                              border
                              backgroundColor="white"
                            >
                              <span style={{ fontWeight: 'bold', fontSize: '90%' }}>
                                {
                                  fw.description ? (
                                    <>
                                      <span>{fw.description}</span>
                                      <br />
                                    </>
                                  ) : null
                                }
                                {
                                  fw.refName ? (
                                    <>
                                      <span>{`${t('VersaoInternaDoBinario')}: `}</span>
                                      <span style={{ fontWeight: 'bold' }}>{fw.fwVers}</span>
                                      <br />
                                    </>
                                  ) : null
                                }
                                {fw.fDate}
                              </span>
                            </ReactTooltip>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </FwVersTable>
            {(!state.fwVersionsRows.length)
              && <div>(nenhum)</div>}
            {(moreThanTen)
              && (
              <Flex justifyContent="center">
                <Box>
                  <ViewMoreArrow viewMore={localState.viewMoreFws} onClick={() => setLocalState({ viewMoreFws: !localState.viewMoreFws })}>
                    <span>{localState.viewMoreFws ? 'Ver menos' : 'Ver mais'}</span>
                    <ArrowIcon />
                  </ViewMoreArrow>
                </Box>
              </Flex>
              )}
          </div>
        </div>
      </Flex>

      {/* Campo de busca/filtro e combo-box de ações para os dispositivos selecionados */}
      <Flex>
        <Box width={1} pt={24} mb={24}>
          <Flex flexWrap="wrap" justifyContent="space-between">
            <Filters
              loading={state.isLoading}
              onClickFilter={onClickFilter}
              onSearchAddition={onSearchAddition}
              onSearchDelete={onSearchDelete}
              render={render}
              state={state}
              clearFilters={props.clearFilters}
            />
            {(checkedDevs.length > 0) && (
              <Box width={[1, 1, 1, 1, 1, 1 / 5]} minWidth="280px">
                {(state.waitingRequest ? <Loader size="small" />
                  : (
                    <Select
                      options={localState.availableActions}
                      value={null}
                      placeholder={`${checkedDevs.length} selecionado(s)`}
                      onSelect={(opt) => applyActionToSelected(opt.value)}
                    />
                  )
                )}
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>

      {/* Modais */}
      {(state.openModal === 'ModalAddFirmware') && <ModalAddFirmware pageState={state} />}
      {(state.openModal === 'ModalSendOtaUpdate') && <ModalSendOtaUpdate pageState={state} fwVersionsRows={state.fwVersionsRows} checkedDevs={checkedDevs} />}
      {(state.openModal === 'ModalUpdateResult') && <ModalUpdateResult pageState={state} />}
      {(state.openModal === 'ModalReqVersResult') && <ModalReqVersResult pageState={state} />}
      {(state.openModal === 'ModalReqCertResult') && <ModalReqCertResult pageState={state} />}
    </>
  );
}

function ModalAddFirmware(props: { pageState: PageState }) {
  const pageState = props.pageState;
  const [state, render, setState] = useStateVar({
    selectedFwFolder: null as null|string,
    file: null as null|(Blob&{name: string}),
    sending: false,
    fwToVersion: '',
    fwFromVersion: '',
    fwRefName: '',
    description: '',
  });
  async function confirmAddFirmware() {
    if (!state.file) {
      toast.error('Selecione um arquivo');
      return;
    }
    if (!state.selectedFwFolder) {
      toast.error('Selecione uma pasta');
      return;
    }
    if (!pageState.fwStageFilter) {
      toast.error('Não foi possível identificar o tipo de firmware');
      return;
    }
    try {
      if (pageState.fwStageFilter !== 'migration') {
        await sendFirmwareProductionAndTest();
      } else {
        await sendFirmwareMigration();
      }
    } catch (err) { console.log(err); toast.error('Houve erro'); }
    state.sending = false;
    render();
  }

  async function sendFirmwareProductionAndTest() {
    if (state.file && state.file.name && state.selectedFwFolder) {
      setState({ sending: true });
      await apiCallFormData('/devs/upload-new-firmware-version', {
        fileName: state.file.name,
        fwStage: pageState.fwStageFilter,
        fwFamily: state.selectedFwFolder,
        description: state.description,
      }, {
        file: state.file,
      });
      window.location.reload();
    }
  }

  async function sendFirmwareMigration() {
    if (state.file && state.file.name && state.selectedFwFolder && state.fwFromVersion && state.fwToVersion) {
      setState({ sending: true });
      await apiCallFormData('/devs/upload-new-firmware-version-migration', {
        fileName: state.file.name,
        fwFamily: state.selectedFwFolder,
        fwFromVersion: state.fwFromVersion,
        fwToVersion: state.fwToVersion,
        description: state.description,
        refName: state.fwRefName,
      }, {
        file: state.file,
      });
      window.location.reload();
    }
  }

  function isValidDeviceMigrationString(str) {
    const regex = /^(dac|dut|dam)\d+_mig_\d+_to_v\d+.bin$/i;

    if (regex.test(str.toLowerCase())) {
      return true;
    }
    return 'Formato inválido. Esperado: "dacX_mig_Y_to_vZ", "dutX_mig_Y_to_vZ" ou "damX_mig_Y_to_vZ".';
  }

  function parseFwRefName() {
    if (state.fwFromVersion && state.fwToVersion && state.selectedFwFolder) {
      const sanitizedFromVersion = state.fwFromVersion.endsWith('_')
        ? state.fwFromVersion.slice(0, -1)
        : state.fwFromVersion;

      return `${state.selectedFwFolder.toLowerCase()}_mig_${sanitizedFromVersion}_to_${state.fwToVersion}`;
    }
  }

  return (
    <ModalWindow onClickOutside={() => { pageState.openModal = null; pageState.renderUpper(); }}>
      <ModalContent>
        <div>
          <Select
            options={pageState.fwFolders}
            value={state.selectedFwFolder}
            placeholder="Pasta"
            onSelect={(item) => {
              state.selectedFwFolder = item;
              state.fwRefName = parseFwRefName() || '';
              render();
            }}
            notNull
          />
        </div>
        <div>&nbsp;</div>
        <div style={{ marginTop: '15px' }}>
          <span style={{ paddingRight: '15px' }}>{(state.file && state.file.name) || ''}</span>
          <FileInput onChange={(e: any) => {
            state.file = e.target.files[0];
            render();
          }}
          >
            <span style={{ display: 'inline-block', width: '200px', textAlign: 'center' }}>
              Selecionar Arquivo
            </span>
            <input type="file" hidden />
          </FileInput>
        </div>
        {pageState.fwStageFilter === 'migration' && (
          <>
            <Flex flexDirection="row" style={{ marginTop: '20px', gap: '50px' }}>
              <Flex flexDirection="column" style={{ width: '200px' }}>
                {t('versaoAtual')}
                <Input
                  type="text"
                  placeholder="Ex: 2_"
                  value={state.fwFromVersion}
                  onChange={(e) => {
                    state.fwFromVersion = e.target.value;
                    state.fwRefName = parseFwRefName() || '';
                    render();
                  }}
                />
              </Flex>
              <Flex flexDirection="column" style={{ width: '200px' }}>
                {t('VersaoDesejada')}
                <Input
                  type="text"
                  placeholder="Ex: v2"
                  value={state.fwToVersion}
                  onChange={(e) => {
                    state.fwToVersion = e.target.value;
                    state.fwRefName = parseFwRefName() || '';
                    render();
                  }}
                />
              </Flex>
            </Flex>
            <Flex flexDirection="column" style={{ marginTop: '20px' }}>
              {t('NomeDeReferenciaDoFirmware')}
              <Input
                type="text"
                disabled
                placeholder="Ex: dac4_mig_1_to_v2"
                value={state.fwRefName}
                onChange={(e) => {
                  state.fwRefName = e.target.value;
                  render();
                }}
              />
            </Flex>
          </>
        )}
        <Flex flexDirection="column" style={{ marginTop: '20px', maxWidth: '600px' }}>
          <span style={{ fontSize: '14px' }}>
            Descrição
          </span>
          <TextArea
            rows={3}
            placeholder="Descreva o que foi alterado nesse firmware"
            value={state.description}
            onChange={(e) => {
              state.description = e.target.value;
              render();
            }}
          />
        </Flex>
        <div>&nbsp;</div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '50px',
        }}
        >
          {state.sending
            ? <Loader size="small" />
            : <Button style={{ width: '120px' }} onClick={confirmAddFirmware} variant="primary">Enviar</Button>}
          <Button
            style={{ width: '120px', margin: '0 20px' }}
            onClick={() => { pageState.openModal = null; pageState.renderUpper(); }}
            variant="grey"
          >
            Cancelar
          </Button>
        </div>
      </ModalContent>
    </ModalWindow>
  );
}

function ModalSendOtaUpdate(props: { fwVersionsRows: FirmwareRow[], checkedDevs: DeviceRow[], pageState: PageState }) {
  const { fwVersionsRows, checkedDevs, pageState } = props;

  const [state, render, setState] = useStateVar({
    firmwareMigrationVersions: [] as {
      DEVICE_MODEL: string;
      FROM_VERSION: string;
      TO_VERSION: string;
      MIGRATION_FOLDER: string;
    }[],
    devIdFamilies: {} as {
      [groupKey: string]: {
        options: FirmwareRow[]
        selected: FirmwareRow
      }
    },
    invalidDevices: {} as {
      [devId: string]: string,
    },
    sending: false,
  });

  useEffect(() => {
    for (const dev of checkedDevs) {
      const groupKey = getDeviceVersionGroupKey(dev);
      if (!groupKey) {
        state.invalidDevices[dev.DEV_ID] = 'Firmware não identificado';
        continue;
      }
      if (!state.devIdFamilies[groupKey]) {
        const compatibleVersions = pageState.fwVersionsRows.filter((fw) => {
          if (pageState.fwStageFilter === 'migration') {
            const { canUpdate } = checkCanUpdateMigration(dev, fw);
            return !!canUpdate;
          }
          const { canUpdate } = checkCanUpdate(dev, fw);
          return !!canUpdate;
        });
        if (compatibleVersions.length === 0) {
          state.invalidDevices[dev.DEV_ID] = 'Nenhuma versão compatível';
          continue;
        }
        state.devIdFamilies[groupKey] = {
          options: compatibleVersions,
          selected: compatibleVersions[0],
        };
      }
      if (pageState.fwStageFilter === 'migration') {
        const { canUpdate } = checkCanUpdateMigration(dev, state.devIdFamilies[groupKey].selected);
        if (!canUpdate) {
          state.invalidDevices[dev.DEV_ID] = 'Versão não compatível';
          continue;
        }
      } else {
        const { canUpdate } = checkCanUpdate(dev, state.devIdFamilies[groupKey].selected);
        if (!canUpdate) {
          state.invalidDevices[dev.DEV_ID] = 'Versão não compatível';
          continue;
        }
      }
    }
    render();
  }, []);

  function checkExistsProductionVersionEqualMigration(dev: DeviceRow, fw: FirmwareRow) {
    // Regex para extrair padrões de migração como: <family>_mig_<from>_to_<to>
    const migrationRegex = new RegExp(`${dev.devIdFamily}_mig_([a-zA-Z0-9_]+)_to_([a-zA-Z0-9_]+)`, 'i');
    const match = fw.refName?.match(migrationRegex);

    if (!match) {
      return false; // Nome do arquivo não segue padrão esperado
    }

    const toVersion = match[2];

    const exists = pageState.fwVersionsAllTypes.some((fwEntry: FirmwareRow) => fwEntry.path.startsWith(`prod/${dev.devIdFamily}`) && fwEntry.fwVers?.startsWith(toVersion));

    return exists;
  }

  async function confirmRequestUpdate() {
    const devs: typeof pageState.modalUpdateResult.devs = [];

    const msgCantUpdate: string[] = [];

    for (const dev of checkedDevs) {
      if (state.invalidDevices[dev.DEV_ID]) continue;
      const groupKey = getDeviceVersionGroupKey(dev)!;
      const fw = state.devIdFamilies[groupKey].selected;
      if (pageState.fwStageFilter === 'migration') {
        const prodIsExist = checkExistsProductionVersionEqualMigration(dev, fw);
        if (!prodIsExist) {
          msgCantUpdate.push(`${t('dispositivo')} ${dev.DEV_ID} (${dev.devIdFamily}) ${t('naoPossuiVersaoDeProducaoParaMigracao')}} ${fw.refName}`);
          continue;
        }
        const { canUpdate, outMsg } = checkCanUpdateMigration(dev, fw);
        if (!canUpdate) {
          msgCantUpdate.push(outMsg);
          continue;
        }
        devs.push({
          devId: dev.DEV_ID,
          fwPath: fw.path,
        });
      } else {
        const { canUpdate, outMsg } = checkCanUpdate(dev, fw);
        if (!canUpdate) {
          msgCantUpdate.push(outMsg);
          continue;
        }
        devs.push({
          devId: dev.DEV_ID,
          fwPath: fw.path,
        });
      }
    }

    if (msgCantUpdate.length > 0) {
      toast.error(`${t('osSeguintesDipositivosNaoPodemSerAlterados')} \n${msgCantUpdate.join(';\n')}`);
      return;
    }

    if (!window.confirm(`Deseja atualizar o firmware de ${devs.length} equipamento(s)?`)) {
      return;
    }

    pageState.modalUpdateResult.devs = devs;
    pageState.openModal = 'ModalUpdateResult';
    pageState.renderUpper();
  }

  return (
    <ModalWindow onClickOutside={() => { pageState.openModal = null; pageState.renderUpper(); }}>
      <ModalContent>
        {Object.keys(state.devIdFamilies).map((groupKey) => {
          const [devIdFamily, , vMajor] = groupKey.split('_');
          return (
            <div key={groupKey} style={{ paddingBottom: '12px' }}>
              <Select
                options={state.devIdFamilies[groupKey].options}
                value={state.devIdFamilies[groupKey].selected}
                placeholder={`${devIdFamily} (major ${vMajor})`}
                propLabel={pageState.fwStageFilter === 'migration' ? 'refNameTranslated' : 'fwVers'}
                onSelect={(item: FirmwareRow) => { state.devIdFamilies[groupKey].selected = item; render(); }}
                notNull
              />
            </div>
          );
        })}
        <div style={{ maxHeight: '115px', overflowY: 'auto' }}>
          {checkedDevs.map((dev) => (
            <div key={dev.DEV_ID} style={state.invalidDevices[dev.DEV_ID] ? { textDecoration: 'line-through' } : {}}>
              [
              {dev.devIdFamily}
              ]&nbsp;
              {dev.DEV_ID}
            </div>
          ))}
        </div>
        <div>&nbsp;</div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '30px',
        }}
        >
          {state.sending
            ? <Loader size="small" />
            : <Button style={{ width: '120px' }} onClick={confirmRequestUpdate} variant="primary">Enviar</Button>}
          <Button
            style={{ width: '120px', margin: '0 20px' }}
            onClick={() => { pageState.openModal = null; pageState.renderUpper(); }}
            variant="grey"
          >
            Cancelar
          </Button>
        </div>
      </ModalContent>
    </ModalWindow>
  );
}

function ModalUpdateResult(props: { pageState: PageState }) {
  const state = props.pageState;
  const [, render_modal] = useStateVar(() => {
    sendAll();
    return {};
  });

  function sendAll(isRetry?: boolean) {
    for (const dev of state.modalUpdateResult.devs) {
      sendCommand(dev, isRetry);
    }
  }

  function sendCommand(dev, isRetry) {
    if (dev.status === 'Comando enviado!') return;
    dev.status = 'Aguardando resposta...';
    Promise.resolve().then(async () => {
      try {
        if (dev.fwPath) {
          const pars = { devId: dev.devId, path: dev.fwPath, waitLonger: isRetry };
          await apiCall('/dev/command-ota', pars);
          dev.status = 'Comando enviado!';
        } else {
          dev.status = 'Não foi possível identificar o firmware!';
        }
      } catch (err) {
        console.log(err);
        dev.status = 'Houve erro';
      }
      render_modal();
    });
  }

  const thereWasErr = state.modalUpdateResult.devs.some((dev) => (dev.status === 'Houve erro'));

  return (
    <ModalWindow onClickOutside={() => { state.openModal = null; state.renderUpper(); }}>
      <ModalContent>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {state.modalUpdateResult.devs.map((dev) => (
            <div>
              {dev.devId}
              :
              {' '}
              {dev.status}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
          {(thereWasErr)
            && (
            <Button style={{ width: '160px', margin: '0 20px' }} onClick={() => { sendAll(true); render_modal(); }} variant="secondary">
              Tentar de novo
            </Button>
            )}
          <Button style={{ width: '120px' }} onClick={() => { window.location.reload(); }} variant="primary">
            Concluir
          </Button>
        </div>
      </ModalContent>
    </ModalWindow>
  );
}

function ModalReqVersResult(props: { pageState: PageState }) {
  const state = props.pageState;
  type SubProps = {
    dev: {
      devId: string
      status?: string
    }
  };
  function DevLine(subProps: SubProps) {
    const { dev } = subProps;
    const [, renderLine] = useState(() => {
      dev.status = 'Aguardando resposta...';
      return {};
    });
    useEffect(() => {
      Promise.resolve().then(async () => {
        try {
          const pars = { devId: dev.devId };
          await apiCall('/dev/request-firmware-info', pars);
          dev.status = 'Recebido!';
        } catch (err) {
          console.log(err);
          dev.status = 'Houve erro';
        }
        renderLine({});
      });
    }, []);
    return (
      <div>
        {dev.devId}
        :
        {' '}
        {dev.status}
      </div>
    );
  }

  return (
    <ModalWindow onClickOutside={() => { state.openModal = null; state.renderUpper(); }}>
      <ModalContent>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {state.modalReqVersResult.devs.map((dev) => <DevLine dev={dev} />)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
          <Button style={{ width: '120px' }} onClick={() => { window.location.reload(); }} variant="primary">
            Concluir
          </Button>
        </div>
      </ModalContent>
    </ModalWindow>
  );
}

function ModalReqCertResult(props: { pageState: PageState }) {
  const state = props.pageState;
  function DevLine({ dev }) {
    const [, renderLine] = useState(() => {
      dev.status = 'Aguardando resposta...';
      return {};
    });
    useEffect(() => {
      Promise.resolve().then(async () => {
        try {
          await apiCall('/request-dev-cert-check', { devId: dev.devId });
          dev.status = 'Recebido!';
        } catch (err) {
          console.log(err);
          dev.status = 'Houve erro';
        }
        renderLine({});
      });
    }, []);
    return (
      <div>
        {dev.devId}
        :
        {' '}
        {dev.status}
      </div>
    );
  }

  return (
    <ModalWindow onClickOutside={() => { state.openModal = null; state.renderUpper(); }}>
      <ModalContent>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {state.modalReqCertResult.devs.map((dev) => <DevLine dev={dev} />)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
          <Button style={{ width: '120px' }} onClick={() => { window.location.reload(); }} variant="primary">
            Concluir
          </Button>
        </div>
      </ModalContent>
    </ModalWindow>
  );
}

export default withTransaction('AdmFirmware', 'component')(AdmFirmware);
